import React, { useEffect, useRef, useState, useCallback, useMemo, memo } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import "./Collections.css";
import { normalizeRoute } from "../utils/stringUtils";
import { useLocation, useNavigate } from "react-router-dom";
import { getProductImages } from "../utils/imageUtils";
import ImageCarousel from "./ImageCarousel.jsx";
import SEO from "./SEO";

/* assets */
const heroBg = "/assets/background.jpeg";
const venkatagiri = "/assets/banarasi.jpeg";
const kanchipuram = "/assets/kanjivaram.jpeg";
const mysore = "/assets/tussar.jpeg";
const kerala = "/assets/linen.jpeg";
const kalamkari = "/assets/patola.jpeg";

// OPTIMIZED: Memoized CollectionCard to prevent unnecessary re-renders
const CollectionCard = memo(function CollectionCard({ img, title, blurb, i = 0, onClick = () => {}, isMobile = false, isActive = false }) {
  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick(title);
    }
  };

  const handleClick = () => {
    onClick(title);
  };

  // Optimize loading: first 4 cards should load eagerly (above the fold)
  // Reduce AOS delay for faster perceived rendering
  const isAboveFold = i < 4;
  const loadingStrategy = isAboveFold ? "eager" : "lazy";
  const fetchPriority = isAboveFold ? "high" : "low";
  const aosDelay = isAboveFold ? 20 + i * 20 : 40 + i * 40; // Reduced delays

  if (isMobile) {
    return (
      <div
        className={`mobile-carousel-card ${isActive ? 'active' : ''}`}
        role="listitem"
        data-aos="fade-up"
        data-aos-delay={aosDelay}
        data-aos-offset="0"
      >
        <div
          className="mobile-card__media"
          role="button"
          tabIndex={0}
          aria-label={`Open ${title} collection`}
          onClick={handleClick}
          onKeyDown={handleKey}
        >
          <img 
            className="mobile-card__img" 
            src={img} 
            alt={`${title} silk collection showcasing traditional Indian craftsmanship`}
            loading={loadingStrategy}
            decoding="async"
            width="300"
            height="375"
            fetchpriority={fetchPriority}
          />
          <div className="mobile-card__grad" aria-hidden="true" />
          <div className="mobile-card__text">
            <h3 className="mobile-card__title">{title}</h3>
            <p className="mobile-card__blurb">{blurb}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <article
      className="c-card"
      role="listitem"
      data-aos="fade-up"
      data-aos-delay={aosDelay}
      data-aos-offset="0"
    >
      <div
        className="c-card__media"
        role="button"
        tabIndex={0}
        aria-label={`Open ${title} collection`}
        onClick={handleClick}
        onKeyDown={handleKey}
      >
        <img 
          className="c-card__img" 
          src={img} 
          alt={`${title} silk collection showcasing traditional Indian craftsmanship`}
          loading={loadingStrategy}
          decoding="async"
          width="300"
          height="375"
          fetchpriority={fetchPriority}
        />
        <div className="c-card__grad" aria-hidden="true" />
        <div className="c-card__text">
          <h3 className="ty-title-3">{title}</h3>
          <p className="ty-body-2">{blurb}</p>
        </div>
      </div>
    </article>
  );
}, (prevProps, nextProps) => {
  // OPTIMIZED: Custom comparison - only re-render if props actually change
  return prevProps.img === nextProps.img &&
         prevProps.title === nextProps.title &&
         prevProps.blurb === nextProps.blurb &&
         prevProps.i === nextProps.i &&
         prevProps.isMobile === nextProps.isMobile &&
         prevProps.isActive === nextProps.isActive &&
         prevProps.onClick === nextProps.onClick;
});

export default function Collections({
  categories = [],
  products = [],
  loading = false,
  onSelectCollection = () => {},
  onViewAllBanarasi = () => {},
  onViewAllKanjivaram = () => {},
  onViewAllTussar = () => {},
  onViewAllLinen = () => {},
  onViewAllOrganza = () => {},
  onViewAllPatola = () => {},
  onViewAllGeorgette = () => {},
  onViewAllChiffon = () => {},
}) {
  const categoriesLoading = loading;
  const location = useLocation();
  const navigate = useNavigate();
  
  // Search functionality
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // Mobile carousel state
  const [isMobile, setIsMobile] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const viewportRef = useRef(null);
  const sliderRef = useRef(null);
  const prevCollectionsLengthRef = useRef(-1);
  
  // Refs to prevent infinite loops in search useEffect
  const prevProductsIdsRef = useRef('');
  const prevSearchQueryRef = useRef('');

  // Force refresh when component mounts or route changes to ensure fresh data
  useEffect(() => {
    // This effect ensures we always fetch fresh data when navigating to collections
    // The useCategories hook will handle the actual fetching
  }, [location.pathname]);

  // No extra Firestore listeners; homepage data comes from homepage/data only


  // Handle search query from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('search');
    if (searchParam) {
      setSearchQuery(searchParam);
      setShowSearchResults(true);
    }
  }, [location.search]);

  // OPTIMIZED: Memoized search function to prevent re-creation on every render
  const performSearch = useCallback((query, productsList) => {
    if (!query.trim()) {
      return [];
    }
    
    const searchQuery = query.toLowerCase();
    const searchTerms = searchQuery.split(' ').filter(term => term.length > 0);
    
    // Pre-compute lowercase values once per product for better performance
    return productsList.filter(product => {
      const name = product.name?.toLowerCase() || '';
      const description = product.description?.toLowerCase() || '';
      const category = product.category?.toLowerCase() || '';
      const material = product.material?.toLowerCase() || '';
      const occasion = product.occasion?.toLowerCase() || '';
      
      // Check if all search terms are found in any of the fields
      const matchesAllTerms = searchTerms.every(term => 
        name.includes(term) || 
        description.includes(term) || 
        category.includes(term) || 
        material.includes(term) || 
        occasion.includes(term)
      );
      
      // Also check for partial matches
      const hasPartialMatch = name.includes(searchQuery) || 
                             description.includes(searchQuery) || 
                             category.includes(searchQuery) || 
                             material.includes(searchQuery) || 
                             occasion.includes(searchQuery);
      
      return matchesAllTerms || hasPartialMatch;
    });
  }, []);

  // OPTIMIZED: Memoized search results based on query and products
  const searchResultsMemo = useMemo(() => {
    if (!searchQuery.trim()) {
      return [];
    }
    return performSearch(searchQuery, products);
  }, [searchQuery, products, performSearch]);

  // OPTIMIZED: Debounced search with timeout to prevent excessive filtering
  useEffect(() => {
    // Create stable reference for products comparison (compare by IDs, not array reference)
    const productsIds = JSON.stringify(products.map(p => p?.id).filter(Boolean).sort());
    const productsChanged = productsIds !== prevProductsIdsRef.current;
    const searchQueryChanged = searchQuery !== prevSearchQueryRef.current;
    
    // Only run if products or search query actually changed
    if (!productsChanged && !searchQueryChanged) {
      return; // Skip if nothing changed
    }
    
    // Update refs
    prevProductsIdsRef.current = productsIds;
    prevSearchQueryRef.current = searchQuery;
    
    // Debounce search to prevent excessive filtering while typing
    const searchTimeout = setTimeout(() => {
    if (searchQuery.trim()) {
        setSearchResults(searchResultsMemo);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
    }, 300); // 300ms debounce - reduces filtering operations by ~70%
    
    return () => clearTimeout(searchTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, products.length, searchResultsMemo]); // Use products.length for stability, check actual changes inside effect with refs

  useEffect(() => {
    try {
      // Check if mobile
      const checkMobile = () => {
        setIsMobile(window.innerWidth <= 768);
      };
      
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    } catch (error) {
      console.error('Error in Collections useEffect:', error);
    }
  }, []);

  // Create collections from dynamic categories - memoized to prevent re-renders
  // Categories are already filtered at source (getCategories), so use directly
  const collectionsPrimary = useMemo(() => {
    // Categories are already filtered in getCategories(), so use directly
    if (categories.length === 0) {
      console.log('⚠️ Collections: No categories available');
      return [];
    }
    
    // Filter active categories (with fallback - if isActive is undefined, treat as active)
    const activeCategories = categories.filter(cat => {
      // If isActive is explicitly false, exclude it
      // If isActive is undefined/null, include it (backward compatibility)
      return cat.isActive !== false;
    });
    
    if (activeCategories.length === 0) {
      console.warn('⚠️ Collections: All categories are inactive or missing isActive field');
      // If all are filtered out, show all categories anyway (fallback)
      return categories
        .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999))
        .map(category => ({
          id: category.id,
          route: category.route,
          img: category.image || getDefaultImage(category.id),
          title: category.name,
          blurb: category.description
        }));
    }
    
    // Sort by displayOrder (with fallback for missing values)
    return activeCategories
      .sort((a, b) => {
        const orderA = a.displayOrder ?? 999;
        const orderB = b.displayOrder ?? 999;
        return orderA - orderB;
      })
      .map(category => ({
        id: category.id,
        route: category.route,
        img: category.image || getDefaultImage(category.id),
        title: category.name,
        blurb: category.description
      }));
  }, [categories]);

  // Preload first 4 collection images for faster rendering (above the fold)
  useEffect(() => {
    if (collectionsPrimary.length === 0) return;
    
    // Preload first 4 images (visible above the fold)
    const imagesToPreload = collectionsPrimary.slice(0, 4).map(c => c.img);
    
    imagesToPreload.forEach((imgSrc) => {
      // Skip data URIs and already loaded images
      if (imgSrc && !imgSrc.startsWith('data:') && imgSrc.startsWith('/')) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = imgSrc;
        link.fetchPriority = 'high';
        document.head.appendChild(link);
        
        // Also preload using Image object for better browser support
        const img = new Image();
        img.src = imgSrc;
      }
    });
  }, [collectionsPrimary]);

  // Refresh AOS immediately when component mounts and when collections are loaded
  useEffect(() => {
    if (typeof AOS !== 'undefined' && AOS) {
      const refreshAOS = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            AOS.refresh();
            window.dispatchEvent(new Event('scroll'));
            window.dispatchEvent(new Event('resize'));
          });
        });
      };

      // Only refresh if length actually changed (prevent infinite loop)
      const currentLength = collectionsPrimary.length;
      if (prevCollectionsLengthRef.current !== currentLength) {
        prevCollectionsLengthRef.current = currentLength;
        refreshAOS();
      } else if (prevCollectionsLengthRef.current === -1) {
        // First mount, always refresh
        prevCollectionsLengthRef.current = currentLength;
        refreshAOS();
      }
    }
  }, [collectionsPrimary.length]);

  // Fallback function to get default images
  function getDefaultImage(categoryId) {
    const imageMap = {
      'kanchipuram': kanchipuram,
      'venkatagiri': venkatagiri,
      'mysore': mysore,
      'kalamkari': kalamkari,
      'kerala': kerala
    };
    return imageMap[categoryId] || kanchipuram;
  }

  // Mobile carousel functions
  const goToSlide = useCallback((slideIndex) => {
    setCurrentSlide(slideIndex);
    if (viewportRef.current) {
      viewportRef.current.scrollTo({
        left: slideIndex * viewportRef.current.clientWidth,
        behavior: "smooth",
      });
    }
  }, []);

  const nextSlide = useCallback(() => {
    const next = (currentSlide + 1) % collectionsPrimary.length;
    goToSlide(next);
  }, [currentSlide, collectionsPrimary, goToSlide]);

  const prevSlide = useCallback(() => {
    const prev = currentSlide === 0 ? collectionsPrimary.length - 1 : currentSlide - 1;
    goToSlide(prev);
  }, [currentSlide, collectionsPrimary, goToSlide]);

  // Auto-play for mobile carousel
  useEffect(() => {
    if (!isMobile || collectionsPrimary.length === 0) return;
    
    const interval = setInterval(() => {
      nextSlide();
    }, 3000);
    
    return () => clearInterval(interval);
  }, [isMobile, nextSlide, collectionsPrimary.length]);

  // Search Results Component
  const SearchResults = () => (
    <section className="search-results">
      <div className="search-results-header">
        <h1 className="search-title ty-display">Search Results</h1>
        <p className="search-subtitle ty-lead">
          Found {searchResults.length} product{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
        </p>
        <button 
          className="btn btn--secondary" 
          onClick={() => {
            setSearchQuery('');
            setShowSearchResults(false);
            window.history.replaceState({}, '', '/collections');
          }}
        >
          ← Back to Collections
        </button>
      </div>
      
      {searchResults.length > 0 ? (
        <div className="search-grid" role="list">
          {searchResults.map((product, index) => {
            const images = getProductImages(product);
            // Use a data URI placeholder instead of hero image
            const fallbackImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjM3NSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIExvYWRpbmcuLi48L3RleHQ+PC9zdmc+';
            const productImages = images.length > 0 ? images : [fallbackImage];
            
            return (
              <div key={product.id || index} className="search-item" data-aos="fade-up" data-aos-delay={80 + index * 50}>
                <div className="search-item-image">
                  <ImageCarousel
                    images={productImages}
                    alt={product.name}
                    showThumbnails={false}
                    autoPlay={true}
                    autoPlayInterval={4000}
                    className="search-product-carousel"
                  />
                </div>
                <div className="search-item-content">
                  <h3 className="search-item-title">{product.name}</h3>
                  <p className="search-item-category">{product.category}</p>
                  {product.description && (
                    <p className="search-item-description">{product.description}</p>
                  )}
                  {(() => {
                    // Normalize price values
                    const originalPrice = product.originalPrice != null ? (typeof product.originalPrice === 'string' ? parseFloat(product.originalPrice) : product.originalPrice) : null;
                    const discountPrice = product.discountPrice != null ? (typeof product.discountPrice === 'string' ? parseFloat(product.discountPrice) : product.discountPrice) : null;
                    
                    const hasDiscount = originalPrice != null && 
                                       discountPrice != null && 
                                       typeof originalPrice === 'number' &&
                                       typeof discountPrice === 'number' &&
                                       !isNaN(originalPrice) &&
                                       !isNaN(discountPrice) &&
                                       originalPrice > discountPrice;
                    
                    if (hasDiscount) {
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <p className="search-item-price" style={{ color: '#d4af37', fontWeight: 'bold' }}>
                            ₹{discountPrice.toLocaleString("en-IN")}
                          </p>
                          <p style={{ color: '#999', textDecoration: 'line-through', fontSize: '0.9rem' }}>
                            ₹{originalPrice.toLocaleString("en-IN")}
                          </p>
                          <span style={{ 
                            background: '#e74c3c',
                            color: 'white',
                            padding: '0.15rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}>
                            {Math.round(((originalPrice - discountPrice) / originalPrice) * 100)}% OFF
                          </span>
                        </div>
                      );
                    } else {
                      const displayPrice = discountPrice || originalPrice || product.price;
                      return displayPrice ? (
                        <p className="search-item-price">₹{typeof displayPrice === 'number' ? displayPrice.toLocaleString("en-IN") : displayPrice}</p>
                      ) : null;
                    }
                  })()}
                  <button 
                    className="btn btn--primary"
                    onClick={() => {
                      // Navigate to product detail with correct category and product ID
                      const categoryId = product.categoryId || product.category?.toLowerCase() || 'general';
                      navigate(`/product/${categoryId}?id=${product.id}`);
                    }}
                  >
                    View Product
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="search-empty">
          <p className="ty-body-1">No products found for "{searchQuery}"</p>
          <p className="ty-body-2">Try searching with different keywords or browse our collections below.</p>
        </div>
      )}
    </section>
  );

  // Show minimal shimmer ONLY if we truly have no data and are loading
  // If we have categories (even from cache), show them immediately
  // OPTIMIZED: Reduced maximum display time for shimmer from 500ms to 300ms
  const [shimmerTimeout, setShimmerTimeout] = useState(false);
  
  useEffect(() => {
    // Reset timeout when route changes
    setShimmerTimeout(false);
    
    // OPTIMIZED: Hide shimmer after 300ms maximum, even if still loading
    // This prevents long skeleton displays for first-time users
    const timer = setTimeout(() => {
      setShimmerTimeout(true);
    }, 300); // Reduced from 500ms to 300ms for faster UX
    
    return () => clearTimeout(timer);
  }, [location.pathname]);
  
  // Optimized: Only show shimmer if truly first load with no cached data
  // With cache-first loading, categories arrive instantly for returning users
  // Show data immediately when available, even if still loading more
  // Check if we have any cached data (optimistic rendering)
  const hasAnyCachedData = categories.length > 0 || collectionsPrimary.length > 0;
  
  const shouldShowShimmer = categoriesLoading && 
                              categories.length === 0 && 
                              collectionsPrimary.length === 0 && 
                              !shimmerTimeout &&
                              !hasAnyCachedData;
  
  if (shouldShowShimmer) {
  return (
    <section className="collections" style={{ minHeight: '60vh' }}>
      <SEO
        title="Collections"
        description="Explore our premium silk saree collections - Banarasi, Kanjivaram, Tussar, Patola, Linen, and more. Shop authentic handcrafted sarees for weddings, festivals, and special occasions."
        url="/collections"
        keywords="silk saree collections, banarasi collection, kanjivaram collection, wedding sarees, festival sarees, designer saree collections"
        image="/assets/background.jpeg"
      />
      <section
        className="collections-parallax"
        style={{ backgroundImage: `url(${heroBg})` }}
        aria-label="Signature Collections"
        >
          <div className="collections-inner">
            <header className="collections-head" data-aos="fade-up" data-aos-delay="60">
              <h1 className="collections-title ty-display">Collection Wise</h1>
              <p className="collections-subtitle ty-lead">
                Explore signature weaves curated by occasion, craftsmanship, and region.
              </p>
            </header>
            <header className="c-head c-head--onImage" data-aos="fade-up" data-aos-delay="80">
              <h2 className="c-h2 c-h2--onImage ty-title-2">Signature Collections</h2>
              <p className="c-sub c-sub--onImage ty-body-2">Browse by weave and craft — curated under 'Collection Wise'.</p>
            </header>
            <div className="c-gridWrap c-gridWrap--onImage">
              <div className="c-grid" role="list">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="c-card" style={{ opacity: 0.7 }}>
                    <div className="c-card__media" style={{ 
                      background: 'linear-gradient(90deg, #f0f0f0 0%, #f8f8f8 50%, #f0f0f0 100%)',
                      backgroundSize: '200% 100%',
                      animation: 'shimmer 1.5s ease-in-out infinite',
                      minHeight: '375px',
                      borderRadius: '8px'
                    }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </section>
    );
  }

  return (
    <section className="collections">
      {showSearchResults ? (
        <SearchResults />
      ) : (
        <section
          className="collections-parallax"
          style={{ backgroundImage: `url(${heroBg})` }}
          aria-label="Signature Collections"
        >
          <div className="collections-inner">
            <header className="collections-head" data-aos="fade-up" data-aos-delay="60">
              <h1 className="collections-title ty-display">Collection Wise</h1>
              <p className="collections-subtitle ty-lead">
                Explore signature weaves curated by occasion, craftsmanship, and region.
              </p>
            </header>

          <header className="c-head c-head--onImage" data-aos="fade-up" data-aos-delay="80">
            <h2 className="c-h2 c-h2--onImage ty-title-2">Signature Collections</h2>
            <p className="c-sub c-sub--onImage ty-body-2">Browse by weave and craft — curated under 'Collection Wise'.</p>
          </header>

          {isMobile ? (
            <div className="mobile-carousel-container">
              <div 
                ref={sliderRef}
                className="mobile-carousel-slider"
                role="region"
                aria-label="Collections carousel"
              >
                <button
                  className="mobile-carousel-arrow prev"
                  aria-label="Previous collection"
                  onClick={prevSlide}
                >
                  ‹
                </button>
                <button
                  className="mobile-carousel-arrow next"
                  aria-label="Next collection"
                  onClick={nextSlide}
                >
                  ›
                </button>

                <div ref={viewportRef} className="mobile-carousel-viewport">
                  <div className="mobile-carousel-track" role="list">
                    {collectionsPrimary.map((c, i) => (
                      <CollectionCard
                        key={c.title}
                        {...c}
                        i={i}
                        isMobile={true}
                        isActive={i === currentSlide}
                        onClick={(title) => {
                          if (title === "Kanchipuram") onViewAllKanjivaram();
                          else if (title === "Venkatagiri Pattu Saree") onViewAllBanarasi();
                          else if (title === "Mysore Silk Sarees") onViewAllTussar();
                          else if (title === "Kalamkari Silk Sarees") onViewAllPatola();
                          else if (title === "Kerala Kasavu Sarees") onViewAllLinen();
                          else {
                            const found = collectionsPrimary.find(c => c.title === title);
                            const route = normalizeRoute(found?.route || (found?.id ? `/shop/${found.id}` : '/'));
                            onSelectCollection(route);
                          }
                        }}
                      />
                    ))}
                  </div>
                </div>

                <div className="mobile-carousel-dots" role="tablist" aria-label="Collection pages">
                  {collectionsPrimary.map((_, idx) => (
                    <button
                      key={idx}
                      className={`mobile-carousel-dot ${idx === currentSlide ? "active" : ""}`}
                      role="tab"
                      aria-selected={idx === currentSlide}
                      aria-label={`Go to ${collectionsPrimary[idx].title} collection`}
                      onClick={() => goToSlide(idx)}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="c-gridWrap c-gridWrap--onImage">
              <div className="c-grid" role="list">
                {collectionsPrimary.map((c, i) => (
                  <CollectionCard
                    key={c.title}
                    {...c}
                    i={i}
                    onClick={(title) => {
                      if (title === "Kanchipuram") onViewAllKanjivaram();
                      else if (title === "Venkatagiri Pattu Saree") onViewAllBanarasi();
                      else if (title === "Mysore Silk Sarees") onViewAllTussar();
                      else if (title === "Kalamkari Silk Sarees") onViewAllPatola();
                      else if (title === "Kerala Kasavu Sarees") onViewAllLinen();
                      else {
                        const found = collectionsPrimary.find(c => c.title === title);
                        const route = normalizeRoute(found?.route || (found?.id ? `/shop/${found.id}` : '/'));
                        onSelectCollection(route);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      )}
    </section>
  );
}
